<template>
  <div>
    <div
      v-if="loadAttorney"
      class="attorney-loader"
    >
      <naj-loader></naj-loader>
    </div>
    <div v-else>
      <naj-box
        :title="local.lawsuitRequest.currentUserAsCaseParty.agentInfo.attorneyInfo"
        :no-v-gutters="true"
        :has-footer="false"
        :no-bottom-margin="true"
      >
        <v-row class="attorney-text">
          <v-col>{{ local.lawsuitRequest.currentUserAsCaseParty.agentInfo.attorneyNumber }}: <span>{{ attorneyDetails.attorneyNumber }}</span></v-col>
          <v-col>{{ local.lawsuitRequest.currentUserAsCaseParty.agentInfo.attorneyType }} : <span>{{ attorneyDetails.attorneyTypeName }}</span></v-col>
          <v-col>{{ local.lawsuitRequest.currentUserAsCaseParty.agentInfo.attorneySource }}: <span>{{ attorneyDetails.attorneyPlaceName }}</span></v-col>
          <v-col v-if="isExternalAttorney">
            <naj-button
              square
              size="small"
              icon="mdi-eye-outline"
              inputs-alignment
              @click="showViewAttorneyModal"
            >
              {{ local.attorney.attorneyDoc }}
            </naj-button>
          </v-col>
        </v-row>
        <v-row
          v-if="isInternalAttorney"
          class="attorney-text"
        >
          <v-col> {{ attorneyDetails.attorneyText }} </v-col>
        </v-row>
      </naj-box>
      <v-row>
        <v-col
          cols="12"
          lg="6"
        >
          <naj-input
            :type-id="formFieldType.multipleSelectBox"
            :label="local.lawsuitRequest.currentUserAsCaseParty.agentInfo.clientsName"
            :value.sync="selectedClientIds"
            :items="attorneyClients"
            item-text="fullName"
            item-value="socialId"
            :disabled="!attorneyClients.length"
            validates="required"
            @change="selectedClientIdsChanged"
          ></naj-input>
        </v-col>
        <v-col
          cols="8"
          lg="3"
        >
          <naj-input
            :type-id="formFieldType.selectbox"
            :label="local.lawsuitRequest.currentUserAsCaseParty.agentInfo[isLawyer ? 'lawyerName' : 'agentName']"
            :value.sync="selectedAgentId"
            :items="attorneyAgents"
            item-text="fullName"
            item-value="socialId"
            :disabled="!attorneyAgents.length"
            :validates="`required|isVerified:${!isLawyer || isValidLawyer}`"
            @change="selectedAgentIdChanged"
          ></naj-input>
        </v-col>
        <v-col
          class="mar-44"
          cols="4"
          lg="3"
        >
          <naj-input
            :type-id="formFieldType.checkbox"
            :label="local.lawsuitRequest.currentUserAsCaseParty.agentInfo[isLawyer ? 'lawyerIsOriginalCaseParty' : 'agentIsOriginalCaseParty']"
            :value.sync="isAgentOriginalCaseParty"
            class="agent-input"
            @change="selectedAgentChanged"
          ></naj-input>
          <div class="agent-note">
            {{ local.lawsuitRequest.currentUserAsCaseParty.agentInfo.agentIsOriginalCasePartyNote }}
          </div>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-tabs
            v-model="tabs"
            hide-slider
          >
            <v-tab
              v-for="(caseParty, n) in caseParties"
              :key="`tab-header-${n}`"
              :href="`#tab-${n}`"
            >
              <div :class="{ 'text-danger': formsValidations[`tab-${n}`] === false }">
                {{ local.lawsuitRequest.currentUserAsCaseParty.agentInfo.completeInfo }} {{ caseParty.firstName }} {{ caseParty.familyName }}
                <span>*</span>
              </div>
            </v-tab>
            <v-tabs-items v-model="tabs">
              <v-tab-item
                v-for="(caseParty, n) in caseParties"
                :key="`tab-body-${n}`"
                :value="`tab-${n}`"
                eager
              >
                <complete-current-user-info-form
                  :ref="`tab-${n}`"
                  :case-party="caseParty"
                ></complete-current-user-info-form>
              </v-tab-item>
            </v-tabs-items>
          </v-tabs>
        </v-col>
      </v-row>
    </div>
    <attorney-modal
      v-if="attorneyDetails.attorneyNumber"
      :modal-visibility="showAttorneyModal"
      :attorney-type="attorneyDetails.attorneyTypeId"
      :attorney-code="attorneyDetails.attorneyNumber.toString()"
      :agent-social-id="``"
      :client-social-id="``"
      @onClose="closeAttorneyModal"
    ></attorney-modal>
  </div>
</template>

<script>
import CompleteCurrentUserInfoForm from './complete-current-user-info-form';
import { casePartyRoleEnum, writingsOfJusticeEnum } from '@/helpers/enum';
import { createNamespacedHelpers } from 'vuex';

const { mapActions: mapSysActions } = createNamespacedHelpers('SystemModule');
import { apiModule } from '@/helpers/utils';
import AttorneyModal from '@/modules/lawsuit/components/attorney-modal';

export default {
  inject: ['$validator'],
  components: {
    CompleteCurrentUserInfoForm,
    AttorneyModal,
  },
  props: {
    agent: {
      type: Object,
      default: () => { },
      required: true,
    },
    clients: {
      type: Array,
      default: () => [],
      required: true,
    },
    attorneyCode: {
      type: [Number, null],
      default: 0,
      required: true,
    },
    isLawyer: {
      type: Boolean,
      default: false,
    },
    formsValidations: {
      type: Object,
      default: () => ({}),
    },
    attorneyDetails: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      debounceTimeout: false,
      loadAttorney: false,
      showAttorneyText: false,
      selectedAgentId: null,
      selectedClientIds: [],
      isAgentOriginalCaseParty: false,
      isValidLawyer: false,
      caseParties: [],
      tabs: null,
      showAttorneyModal: false,
      writingsOfJusticeEnum,
      apiModule,
    };
  },
  computed: {
    attorneyClients() {
      return (this.attorneyDetails || {}).firstParties || [];
    },
    attorneyAgents() {
      return (this.attorneyDetails || {}).secondParties || [];
    },
    isInternalAttorney() {
      return (this.attorneyDetails || {}).attorneyTypeId == writingsOfJusticeEnum.internalAttorney;
    },
    isExternalAttorney() {
      return (this.attorneyDetails || {}).attorneyTypeId == writingsOfJusticeEnum.externalAttorney;
    },
  },
  mounted() {
    this.checkAddress();
  },
  methods: {
    ...mapSysActions(['checkPersonalAddress', 'validateLawyer']),
    checkAddress() {
      [...this.attorneyDetails.firstParties, ...this.attorneyDetails.secondParties].forEach((p) => {
        this.checkPersonalAddress(p.socialId)
          .then((address) => {
            this.$set(p, 'nationalAddress', {
              ...p.nationalAddress,
              ...address.nationalAddress,
            });
            this.$set(p, 'nationalAddresses', address.nationalAddresses);
          })
          .catch(() => { this.toaster.error(this.local.lawsuitRequest.currentUserAsCaseParty.agentInfo.nationalAddressError(p.fullName)); });
      });
    },
    selectedAgentIdChanged() {
      if (this.isLawyer) {
        this.isValidLawyer = false;
        if (this.selectedAgentId) {
          Promise.any([
            this.validateLawyer({
              socialId: this.selectedAgentId,
              isTraineeLawyer: false,
              overrideErrorHandlingBehaviour: true,
            }),
            this.validateLawyer({
              socialId: this.selectedAgentId,
              isTraineeLawyer: true,
              overrideErrorHandlingBehaviour: true,
            }),
          ]).then(() => {
            this.isValidLawyer = true;
          }).catch((err) => {
            if (err.errors) {
              this.toaster.error(err.errors[0]?.message);
            }
          });
        }
      }
      this.selectedAgentChanged();
    },
    selectedAgentChanged() {
      const agent = this.attorneyAgents.find(a => a.socialId === this.selectedAgentId);
      const clients = (this.selectedClientIds || []).map(cl => this.attorneyClients.find(c => c.socialId === cl));
      this.caseParties = [...clients];
      if (agent) {
        this.$set(agent, 'casePartyRole', this.isAgentOriginalCaseParty ? casePartyRoleEnum.prosecutor : 0);
        this.$push(this.caseParties, agent);
      }
      this.$emit('update:agent', agent || {});
    },
    selectedClientIdsChanged() {
      const agent = this.attorneyAgents.find(a => a.socialId === this.selectedAgentId);
      const clients = (this.selectedClientIds || []).map(cl => this.attorneyClients.find(c => c.socialId === cl));
      this.caseParties = [...clients];
      if (agent) {
        this.$set(agent, 'casePartyRole', this.isAgentOriginalCaseParty ? casePartyRoleEnum.prosecutor : 0);
        this.$push(this.caseParties, agent);
      }
      this.$emit('update:clients', clients);
    },
    showViewAttorneyModal() {
      this.showAttorneyModal = true;
    },
    closeAttorneyModal() {
      this.showAttorneyModal = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.mar-44 {
  margin-top: 44px !important;
}
.attorney-loader ::v-deep {
  padding: 15px;
  .naj-loader {
    top: 48%;
  }
}

.attorney-text {
  font-size: 16px;
  line-height: 1.8;
  color: #6d6d6d;
  span {
    font-weight: bold;
  }
}
.agent-input {
  margin: -24px -3px 5px;
}
.agent-note {
  color: #ff3547;
  border: 1px solid #ff3547;
  padding: 5px;
  font-size: 13px;
  text-align: center;
}

::v-deep {
  .v-tabs > .v-tabs-bar,
  .v-tabs-items {
    background-color: transparent !important;
  }
  .v-tabs-bar {
    margin-bottom: 15px;
  }
  .v-slide-group__wrapper {
    overflow: initial;
    contain: initial;
  }
  .v-tab {
    padding: 5px;
    background-color: rgba(255, 255, 255, 0.04);
    margin-left: 10px;
    border-bottom: 3px solid #ddd;
    background-color: #eee;
    &.v-tab--active {
      background-color: #fff;
      border-bottom-color: #009587;
      position: relative;
      &:after {
        content: '';
        display: inline-block;
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-8px);
        border-top: 8px solid #009587;
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
      }
    }
  }
}
</style>
